import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

// ── Types ─────────────────────────────────────────────────────────────────

interface ModelStat {
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
}

interface SiteStat {
  siteId: number;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  models: string[];
}

interface MonthlyTotal {
  year: number;
  month: number;
  calls: number;
  estimatedCostUsd: number;
}

interface SiteOverride {
  siteId: number;
  value: string;
  updatedBy: string;
  updatedAt: string;
}

interface FeedbackItem {
  id: number;
  siteId: number;
  feedbackText: string;
  triggerSource: string;
  sentimentScore: number | null;
  userEmail: string;
  pageContext: string;
  createdAt: string;
}

interface ActionLogItem {
  id: number;
  toolName: string;
  summary: string;
  triggeredBy: string;
  isRolledBack: boolean;
  rolledBackAt: string | null;
  rolledBackBy: string | null;
  createdAt: string;
}

interface AdminStats {
  days: number;
  activeModel: string;
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalEstimatedCostUsd: number;
  thisMonthCalls: number;
  thisMonthEstimatedCostUsd: number;
  feedbackCount: number;
  frustrationCount: number;
  byModel: ModelStat[];
  bySite: SiteStat[];
  monthlyTotals: MonthlyTotal[];
  siteOverrides: SiteOverride[];
  recentFeedback: FeedbackItem[];
  knowledgeChunks: number;
  knowledgeFiles: number;
}

// ── Model options ─────────────────────────────────────────────────────────

const MODEL_OPTIONS = [
  { id: "claude-haiku-4-5-20251001", label: "Haiku — cheapest", inputPer1k: "$0.0008", outputPer1k: "$0.004" },
  { id: "claude-sonnet-4-6",          label: "Sonnet — balanced", inputPer1k: "$0.003",  outputPer1k: "$0.015" },
  { id: "claude-opus-4-6",            label: "Opus — most capable", inputPer1k: "$0.015", outputPer1k: "$0.075" },
];

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ── Helpers ───────────────────────────────────────────────────────────────

const formatCost = (usd: number) =>
  usd === 0 ? "$0.00" : usd < 0.01 ? `<$0.01` : `$${usd.toFixed(2)}`;

const formatCostExact = (usd: number) =>
  usd === 0 ? "$0.000000" : `$${usd.toFixed(6)}`;

const formatTokens = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` :
  n >= 1_000     ? `${(n / 1_000).toFixed(1)}K` : `${n}`;

const modelShort = (m: string) =>
  m.includes("haiku") ? "Haiku" : m.includes("sonnet") ? "Sonnet" : m.includes("opus") ? "Opus" : m;

const triggerLabel = (t: string) =>
  t === "frustration_detected" ? "Auto — frustration" :
  t === "sentiment_api"        ? "Auto — sentiment"  : "User submitted";

// ── Main component ────────────────────────────────────────────────────────

export default function CopilotAdmin() {
  const api = useApi();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState<"usage"|"sites"|"feedback"|"knowledge"|"elevenlabs"|"actions">("usage");

  // Action logs
  const [actionLogs, setActionLogs] = useState<ActionLogItem[]>([]);
  const [actionLogsLoading, setActionLogsLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState<number | null>(null);
  const [rollbackMsg, setRollbackMsg] = useState<string | null>(null);

  // Global model
  const [selectedModel, setSelectedModel] = useState("");
  const [savingModel, setSavingModel] = useState(false);
  const [modelSaved, setModelSaved] = useState(false);

  // Site override state
  const [overrideSiteId, setOverrideSiteId] = useState("");
  const [overrideModel, setOverrideModel] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  const [overrideSaved, setOverrideSaved] = useState<string | null>(null);

  // KB toggle
  const [kbEnabled, setKbEnabled] = useState(false);
  const [kbToggleSaving, setKbToggleSaving] = useState(false);

  // Ingest
  const [ingesting, setIngesting] = useState(false);
  const [ingestResult, setIngestResult] = useState<string | null>(null);

  // KB one-time reindex
  const [reindexing, setReindexing] = useState(false);
  const [reindexResult, setReindexResult] = useState<string | null>(null);
  const [vectorBackfilling, setVectorBackfilling] = useState(false);
  const [vectorResult, setVectorResult] = useState<string | null>(null);
  const [relinkTenantId, setRelinkTenantId] = useState("");
  const [relinking, setRelinking] = useState(false);
  const [relinkResult, setRelinkResult] = useState<string | null>(null);

  // ElevenLabs
  const [elStats, setElStats] = useState<{ totalCharacters: number; totalEstimatedCostUsd: number; bySite: { siteId: number; characters: number; estimatedCostUsd: number }[] } | null>(null);
  const [elLoading, setElLoading] = useState(false);

  useEffect(() => { loadStats(); }, [days]);

  useEffect(() => {
    if (activeTab !== "elevenlabs" || elStats) return;
    setElLoading(true);
    api.admin.get(`ElevenLabs/AdminStats?days=${days}`)
      .then((r) => r.json())
      .then((d) => setElStats(d))
      .catch(() => {})
      .finally(() => setElLoading(false));
  }, [activeTab, days]);

  useEffect(() => {
    if (activeTab !== "actions") return;
    setActionLogsLoading(true);
    api.admin.get("Copilot/Actions")
      .then((r) => r.json())
      .then((d) => setActionLogs(Array.isArray(d) ? d : []))
      .catch(() => setActionLogs([]))
      .finally(() => setActionLogsLoading(false));
  }, [activeTab]);

  const rollbackAction = async (id: number) => {
    setRollingBack(id); setRollbackMsg(null);
    try {
      const res = await api.admin.post(`Copilot/Actions/${id}/Rollback`, {});
      const data = await res.json();
      if (data.success) {
        setRollbackMsg(data.message ?? "Rolled back.");
        setActionLogs((prev) => prev.map((a) => a.id === id ? { ...a, isRolledBack: true } : a));
      } else {
        setRollbackMsg(data.error ?? "Rollback failed.");
      }
    } catch {
      setRollbackMsg("Rollback request failed.");
    } finally {
      setRollingBack(null);
      setTimeout(() => setRollbackMsg(null), 5000);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.get(`Copilot/AdminStats?days=${days}`);
      const data = await res.json();
      setStats(data);
      if (data.activeModel && !selectedModel) setSelectedModel(data.activeModel);
      // Load KB toggle
      try {
        const sRes = await api.admin.get("Copilot/Settings");
        if (sRes.ok) {
          const arr: Array<{ Key?: string; key?: string; Value?: string; value?: string }> = await sRes.json();
          const kb = arr.find((s) => (s.Key ?? s.key) === "enableKbIngestion");
          setKbEnabled((kb?.Value ?? kb?.value) === "true");
        }
      } catch { /* non-fatal */ }
    } catch {
      setError("Could not load Copilot stats. You may not have admin access.");
    } finally {
      setLoading(false);
    }
  };

  const saveGlobalModel = async () => {
    setSavingModel(true); setModelSaved(false);
    try {
      await api.admin.post("Copilot/Settings", { key: "model", value: selectedModel, siteId: 0 });
      setModelSaved(true);
      setTimeout(() => setModelSaved(false), 3000);
      loadStats();
    } catch { setError("Could not save model."); }
    finally { setSavingModel(false); }
  };

  const saveSiteOverride = async () => {
    const sid = parseInt(overrideSiteId, 10);
    if (!sid || !overrideModel) return;
    setSavingOverride(true);
    try {
      await api.admin.post("Copilot/Settings", { key: "model", value: overrideModel, siteId: sid });
      setOverrideSaved(`Site ${sid} → ${modelShort(overrideModel)}`);
      setTimeout(() => setOverrideSaved(null), 4000);
      setOverrideSiteId(""); setOverrideModel("");
      loadStats();
    } catch { setError("Could not save site override."); }
    finally { setSavingOverride(false); }
  };

  const removeSiteOverride = async (siteId: number) => {
    try {
      await api.admin.post("Copilot/Settings", { key: "model", value: "", siteId });
      loadStats();
    } catch { setError("Could not remove override."); }
  };

  const triggerIngest = async () => {
    setIngesting(true); setIngestResult(null);
    try {
      const res = await api.admin.post("Copilot/Ingest", {});
      const data = await res.json();
      setIngestResult(data.message ?? `${data.chunks} chunks (${data.mdChunks ?? 0} docs + ${data.kbArticles ?? 0} KB articles). Ingest running in background — refresh in ~30s.`);
      loadStats();
    } catch { setIngestResult("Ingest trigger failed."); }
    finally { setIngesting(false); }
  };

  const checkIngestStatus = async () => {
    setIngesting(true); setIngestResult(null);
    try {
      const res = await api.admin.get("Copilot/Ingest/Status");
      const data = await res.json();
      setIngestResult(data.message ?? `${data.chunks} chunks indexed.`);
    } catch { setIngestResult("Status check failed."); }
    finally { setIngesting(false); }
  };

  // Monthly chart helpers
  const maxMonthCost = Math.max(1, ...(stats?.monthlyTotals ?? []).map(m => m.estimatedCostUsd));

  if (loading) return <div className="p-8 text-slate-400 text-center">Loading Copilot admin stats...</div>;
  if (error) return <div className="p-8"><div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-400">{error}</div></div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Velaro Copilot — Admin</h1>
          <p className="text-slate-400 text-sm mt-1">Model config · Cost tracking per tenant · Feedback · Knowledge base</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-slate-400 text-sm">Period:</label>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="bg-slate-800 border border-slate-600 text-white rounded px-2 py-1 text-sm">
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </div>
      </div>

      {/* This-month banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "This month — calls", value: (stats?.thisMonthCalls ?? 0).toLocaleString(), highlight: false },
          { label: "This month — est. cost", value: formatCost(stats?.thisMonthEstimatedCostUsd ?? 0), highlight: true },
          { label: `${days}d calls`, value: (stats?.totalCalls ?? 0).toLocaleString(), highlight: false },
          { label: `${days}d est. cost`, value: formatCost(stats?.totalEstimatedCostUsd ?? 0), highlight: true },
        ].map((s) => (
          <div key={s.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-center">
            <div className={`text-2xl font-bold ${s.highlight ? "text-amber-400" : "text-white"}`}>{s.value}</div>
            <div className="text-slate-400 text-xs mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Model Config */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h2 className="text-white font-semibold mb-1">Model Configuration</h2>
        <p className="text-slate-400 text-sm mb-4">
          Global default applies to all tenants. Site overrides take priority — useful for giving premium sites Sonnet while keeping Haiku for everyone else.
        </p>

        {/* Global model */}
        <div className="mb-5">
          <div className="text-slate-300 text-xs font-semibold uppercase tracking-wide mb-2">Global Default</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {MODEL_OPTIONS.map((m) => (
              <button key={m.id}
                onClick={() => setSelectedModel(m.id)}
                className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                  selectedModel === m.id
                    ? "border-blue-500 bg-blue-900/30 text-white"
                    : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"
                }`}>
                <span className="font-medium">{m.label}</span>
                <span className="ml-2 text-xs opacity-60">in:{m.inputPer1k} out:{m.outputPer1k}/1K</span>
                {stats?.activeModel === m.id && (
                  <span className="ml-2 text-xs bg-blue-700 text-white rounded-full px-1.5">active</span>
                )}
              </button>
            ))}
          </div>
          <button onClick={saveGlobalModel}
            disabled={savingModel || selectedModel === stats?.activeModel}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded px-4 py-1.5 text-sm font-medium transition-colors">
            {savingModel ? "Saving..." : "Set Global Default"}
          </button>
          {modelSaved && <span className="ml-3 text-green-400 text-sm">Saved!</span>}
        </div>

        {/* Site overrides */}
        <div>
          <div className="text-slate-300 text-xs font-semibold uppercase tracking-wide mb-2">Site Overrides</div>
          {Array.isArray(stats?.siteOverrides) && stats.siteOverrides.length > 0 ? (
            <div className="mb-3 space-y-2">
              {stats.siteOverrides.map((o) => (
                <div key={o.siteId} className="flex items-center gap-3 bg-slate-900/60 rounded-lg px-4 py-2 border border-slate-700">
                  <span className="text-slate-400 text-sm">Site {o.siteId}</span>
                  <span className="text-white text-sm font-medium">{modelShort(o.value)}</span>
                  <span className="text-slate-500 text-xs">by {o.updatedBy}</span>
                  <button onClick={() => removeSiteOverride(o.siteId)}
                    className="ml-auto text-xs text-red-400 hover:text-red-300 transition-colors">
                    Remove (use global)
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm mb-3">No site overrides — all sites use the global default.</p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <input type="number" value={overrideSiteId}
              onChange={(e) => setOverrideSiteId(e.target.value)}
              placeholder="Site ID"
              className="w-28 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
            <select value={overrideModel} onChange={(e) => setOverrideModel(e.target.value)}
              className="bg-slate-900 border border-slate-600 text-white rounded px-2 py-1.5 text-sm">
              <option value="">— pick model —</option>
              {MODEL_OPTIONS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <button onClick={saveSiteOverride}
              disabled={savingOverride || !overrideSiteId || !overrideModel}
              className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded px-3 py-1.5 text-sm transition-colors">
              {savingOverride ? "Saving..." : "Set Override"}
            </button>
            {overrideSaved && <span className="text-green-400 text-sm">Saved: {overrideSaved}</span>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700">
        {(["usage","sites","feedback","knowledge","elevenlabs","actions"] as const).map((t) => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
              activeTab === t ? "border-blue-500 text-white" : "border-transparent text-slate-400 hover:text-white"
            }`}>
            {t === "sites" ? "Per-Site Costs" : t === "knowledge" ? "Knowledge Base" : t === "elevenlabs" ? "ElevenLabs Voice" : t === "actions" ? "Action Log" : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab: Usage */}
      {activeTab === "usage" && (
        <div className="space-y-5">
          {/* Monthly bar chart */}
          {Array.isArray(stats?.monthlyTotals) && stats.monthlyTotals.length > 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-4">Rolling 12-Month Cost</h3>
              <div className="flex items-end gap-2 h-32">
                {stats.monthlyTotals.map((m) => {
                  const pct = (m.estimatedCostUsd / maxMonthCost) * 100;
                  const isThisMonth = m.year === new Date().getFullYear() && m.month === new Date().getMonth() + 1;
                  return (
                    <div key={`${m.year}-${m.month}`} className="flex-1 flex flex-col items-center gap-1 group relative">
                      <div className="w-full rounded-t transition-all"
                        style={{
                          height: `${Math.max(4, pct)}%`,
                          backgroundColor: isThisMonth ? "#3b82f6" : "#374151",
                          minHeight: "4px"
                        }} />
                      <span className="text-slate-500 text-xs">{MONTH_NAMES[m.month - 1]}</span>
                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                        {MONTH_NAMES[m.month - 1]} {m.year}<br/>{m.calls} calls · {formatCost(m.estimatedCostUsd)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* By model */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <h3 className="text-white font-semibold mb-4">Usage by Model ({days} days)</h3>
            {Array.isArray(stats?.byModel) && stats.byModel.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 text-slate-400 text-xs">
                    <th className="text-left py-2">Model</th>
                    <th className="text-right py-2">Calls</th>
                    <th className="text-right py-2">Input</th>
                    <th className="text-right py-2">Output</th>
                    <th className="text-right py-2">Est. Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.byModel.map((row) => (
                    <tr key={row.model} className="border-b border-slate-800 text-slate-300">
                      <td className="py-2 font-mono text-xs">{row.model}</td>
                      <td className="py-2 text-right">{row.calls.toLocaleString()}</td>
                      <td className="py-2 text-right">{formatTokens(row.inputTokens)}</td>
                      <td className="py-2 text-right">{formatTokens(row.outputTokens)}</td>
                      <td className="py-2 text-right text-amber-400 font-mono text-xs">{formatCostExact(row.estimatedCostUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-slate-500 text-sm text-center py-4">No usage data in this period.</p>
            )}
          </div>
        </div>
      )}

      {/* Tab: Per-Site Costs */}
      {activeTab === "sites" && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-1">Cost per Tenant ({days} days)</h3>
          <p className="text-slate-400 text-sm mb-4">Sorted by estimated cost descending. Costs are estimates based on per-token pricing at time of each call.</p>
          {Array.isArray(stats?.bySite) && stats.bySite.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-slate-400 text-xs">
                  <th className="text-left py-2">Site ID</th>
                  <th className="text-right py-2">Calls</th>
                  <th className="text-right py-2">Input</th>
                  <th className="text-right py-2">Output</th>
                  <th className="text-right py-2">Est. Cost</th>
                  <th className="text-left py-2 pl-4">Model(s)</th>
                  <th className="text-right py-2">Override</th>
                </tr>
              </thead>
              <tbody>
                {stats.bySite.map((row) => {
                  const override = stats.siteOverrides?.find(o => o.siteId === row.siteId);
                  return (
                    <tr key={row.siteId} className="border-b border-slate-800 text-slate-300 hover:bg-slate-700/30">
                      <td className="py-2 font-mono text-xs">{row.siteId}</td>
                      <td className="py-2 text-right">{row.calls.toLocaleString()}</td>
                      <td className="py-2 text-right text-xs">{formatTokens(row.inputTokens)}</td>
                      <td className="py-2 text-right text-xs">{formatTokens(row.outputTokens)}</td>
                      <td className="py-2 text-right text-amber-400 font-mono text-xs">{formatCostExact(row.estimatedCostUsd)}</td>
                      <td className="py-2 pl-4 text-xs text-slate-400">{(row.models ?? []).map(modelShort).join(", ")}</td>
                      <td className="py-2 text-right">
                        {override ? (
                          <span className="text-blue-400 text-xs">{modelShort(override.value)}</span>
                        ) : (
                          <span className="text-slate-600 text-xs">global</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-slate-500 text-sm text-center py-8">No per-site data yet — usage will appear here after the first Copilot chats.</p>
          )}
        </div>
      )}

      {/* Tab: Feedback */}
      {activeTab === "feedback" && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Feedback Inbox ({days} days)</h3>
            <div className="flex gap-4 text-sm">
              <span className="text-slate-400">Total: <span className="text-white font-semibold">{stats?.feedbackCount ?? 0}</span></span>
              <span className="text-amber-400">Frustration triggered: <span className="font-semibold">{stats?.frustrationCount ?? 0}</span></span>
            </div>
          </div>
          {Array.isArray(stats?.recentFeedback) && stats.recentFeedback.length > 0 ? (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {stats.recentFeedback.map((fb) => (
                <div key={fb.id} className="bg-slate-900/60 rounded-lg p-4 border border-slate-700">
                  <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                    <div className="flex gap-2 items-center flex-wrap">
                      <span className="text-slate-500 text-xs font-mono">Site {fb.siteId}</span>
                      {fb.userEmail && <span className="text-slate-400 text-xs">{fb.userEmail}</span>}
                      <span className={`text-xs rounded-full px-2 py-0.5 ${
                        fb.triggerSource === "frustration_detected"
                          ? "bg-amber-900/50 text-amber-400"
                          : "bg-blue-900/50 text-blue-400"
                      }`}>{triggerLabel(fb.triggerSource)}</span>
                      {fb.pageContext && <span className="text-slate-500 text-xs">{fb.pageContext}</span>}
                    </div>
                    <span className="text-slate-500 text-xs whitespace-nowrap">{new Date(fb.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p className="text-slate-200 text-sm">{fb.feedbackText}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm text-center py-8">No feedback in this period.</p>
          )}
        </div>
      )}

      {/* Tab: ElevenLabs Voice */}
      {activeTab === "elevenlabs" && (
        <div className="space-y-5">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: `${days}d characters`, value: (elStats?.totalCharacters ?? 0).toLocaleString() },
              { label: `${days}d est. cost`, value: formatCost(elStats?.totalEstimatedCostUsd ?? 0), highlight: true },
              { label: "pricing", value: "$0.03/1K chars (Turbo)", note: true },
            ].map((s) => (
              <div key={s.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4 text-center">
                <div className={`text-2xl font-bold ${s.highlight ? "text-amber-400" : "text-white"}`}>{s.value}</div>
                <div className="text-slate-400 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          {elLoading ? (
            <div className="text-slate-400 text-center py-8">Loading ElevenLabs stats...</div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
              <h3 className="text-white font-semibold mb-4">Voice Usage by Site ({days} days)</h3>
              {Array.isArray(elStats?.bySite) && elStats.bySite.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700 text-slate-400 text-xs">
                      <th className="text-left py-2">Site ID</th>
                      <th className="text-right py-2">Characters</th>
                      <th className="text-right py-2">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {elStats.bySite.map((row) => (
                      <tr key={row.siteId} className="border-b border-slate-800 text-slate-300 hover:bg-slate-700/30">
                        <td className="py-2 font-mono text-xs">{row.siteId}</td>
                        <td className="py-2 text-right">{row.characters.toLocaleString()}</td>
                        <td className="py-2 text-right text-amber-400 font-mono text-xs">{formatCostExact(row.estimatedCostUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-slate-500 text-sm text-center py-8">
                  No voice usage yet. Enable voice on an AI bot config to start generating TTS usage.
                </p>
              )}
            </div>
          )}

          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 text-sm text-slate-400 space-y-2">
            <div className="text-white font-medium">ElevenLabs Pricing Reference</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[
                { model: "eleven_turbo_v2_5",      label: "Turbo v2.5",      price: "$0.030/1K" },
                { model: "eleven_flash_v2_5",      label: "Flash v2.5",      price: "$0.015/1K" },
                { model: "eleven_multilingual_v2", label: "Multilingual v2", price: "$0.030/1K" },
              ].map((m) => (
                <div key={m.model} className="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                  <div className="text-white text-xs font-medium">{m.label}</div>
                  <div className="text-amber-400 text-xs font-mono mt-1">{m.price} chars</div>
                  <div className="text-slate-500 text-xs mt-1 font-mono">{m.model}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Enable voice per bot in AI Bot Settings → General → Voice Settings.
              Gate by site: toggle <code className="text-green-400 bg-slate-800 px-1 rounded text-xs">EnableBotVoice</code> in Database Tool.
            </p>
          </div>
        </div>
      )}

      {/* Tab: Knowledge Base */}
      {activeTab === "knowledge" && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-5">
          <div>
            <h3 className="text-white font-semibold mb-4">Knowledge Base Status</h3>
            <div className="flex gap-8 mb-4">
              <div>
                <div className="text-3xl font-bold text-white">{stats?.knowledgeChunks ?? 0}</div>
                <div className="text-slate-400 text-sm">chunks indexed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">{stats?.knowledgeFiles ?? 0}</div>
                <div className="text-slate-400 text-sm">source files</div>
              </div>
            </div>
          </div>

          <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-4 text-sm space-y-2">
            <div className="text-white font-medium">Where to put knowledge files</div>
            <p className="text-slate-400">
              Drop any <code className="text-green-400 bg-slate-800 px-1 py-0.5 rounded text-xs">.md</code> file into the{" "}
              <code className="text-green-400 bg-slate-800 px-1 py-0.5 rounded text-xs">copilot-kb/</code> folder in the repo root{" "}
              (<code className="text-slate-400 bg-slate-800 px-1 py-0.5 rounded text-xs">C:\Users\alex\velaro-admin\copilot-kb\</code>).
              The ingest service picks it up on next app startup or within 24 hours. No code change needed.
            </p>
            <p className="text-slate-400">
              Supplementary docs also scanned from{" "}
              <code className="text-green-400 bg-slate-800 px-1 py-0.5 rounded text-xs">docs/</code> — for files like the variables guide.
              Do <strong className="text-white">not</strong> put internal planning docs (todos, architecture notes) in either folder — they will be served to customers.
            </p>
            <p className="text-slate-400">
              <strong className="text-white">Ingest frequency:</strong> on app startup (15s delay) + every 24 hours automatically. Content changes are detected by comparison — unchanged files are skipped.
            </p>
            <p className="text-slate-400">
              <strong className="text-white">How it works:</strong> each <code className="text-green-400 bg-slate-800 px-1 rounded text-xs">##</code> heading becomes one chunk (max ~1,500 chars). Keywords are extracted and stored. When a customer asks a question, the top 6 matching chunks are injected into the Claude system prompt as "RELEVANT DOCUMENTATION". Claude answers only from that content.
            </p>
          </div>

          {/* KB article ingestion toggle */}
          <div className="border border-slate-700 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-white text-sm font-medium mb-1">v20 KB Article Ingestion</div>
                <p className="text-slate-400 text-xs">
                  When enabled, published v20 Knowledge Base articles will be ingested alongside copilot-kb/ and docs/.
                </p>
                <p className="text-amber-500 text-xs mt-2">
                  Pending: KB article source filter not yet implemented. Enable once v20-specific articles are published.
                </p>
              </div>
              <button
                onClick={async () => {
                  const next = !kbEnabled;
                  setKbToggleSaving(true);
                  try {
                    await api.admin.post("Copilot/Settings", { key: "enableKbIngestion", value: next ? "true" : "false", siteId: 0 });
                    setKbEnabled(next);
                  } catch { /* non-fatal */ } finally { setKbToggleSaving(false); }
                }}
                disabled={kbToggleSaving}
                className={`flex-shrink-0 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  kbEnabled ? "bg-green-700 hover:bg-green-800 text-white" : "bg-slate-700 hover:bg-slate-600 text-slate-300"
                }`}>
                {kbToggleSaving ? "Saving..." : kbEnabled ? "Enabled" : "Disabled"}
              </button>
            </div>
          </div>

          {/* Trigger + status buttons */}
          <div className="flex gap-2 flex-wrap">
            <button onClick={triggerIngest} disabled={ingesting}
              className="bg-blue-700 hover:bg-blue-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded px-4 py-2 text-sm transition-colors">
              {ingesting ? "Triggering..." : "Force Re-ingest Now"}
            </button>
            <button onClick={checkIngestStatus} disabled={ingesting}
              className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded px-4 py-2 text-sm transition-colors">
              Check Status
            </button>
          </div>
          <p className="text-slate-500 text-xs">
            "Force Re-ingest Now" rescans copilot-kb/ .md files + all published KB articles with Bot Response enabled, and updates the SQL chunk table immediately (runs in background ~30s). This is the same pipeline the copilot queries. Runs automatically on startup + every 24h.
          </p>
          {ingestResult && (
            <div className="text-sm text-slate-300 bg-slate-900/60 rounded p-3 border border-slate-700">{ingestResult}</div>
          )}

          {/* ── KB tenant relink (for migrated tenants) ───────────────────── */}
          <div className="border border-slate-700 rounded-lg p-4 space-y-3">
            <div className="text-white text-sm font-semibold">Force-Relink KB Tenant</div>
            <p className="text-slate-400 text-xs">
              If KB articles were migrated from V10 and the admin UI shows nothing, the tenant ID isn't linked to this site in SiteConfiguration. Enter the tenantId from the KBTenants table to link it. After relinking, hit "Force Re-ingest Now" above.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                className="flex-1 bg-slate-900 border border-slate-600 rounded px-2 py-1.5 text-xs text-white font-mono placeholder-slate-500 focus:outline-none focus:border-blue-500"
                placeholder="tenant-id (e.g. a1b2c3d4-...)"
                value={relinkTenantId}
                onChange={(e) => setRelinkTenantId(e.currentTarget.value)}
              />
              <button
                disabled={relinking || !relinkTenantId.trim()}
                onClick={async () => {
                  setRelinking(true); setRelinkResult(null);
                  try {
                    const res = await api.admin.put("api/kb/tenants/relink", { tenantId: relinkTenantId.trim() });
                    const data = await res.json().catch(() => ({}));
                    if (res.ok) {
                      setRelinkResult(`✓ Linked: ${(data as any).CompanyName ?? relinkTenantId} — now hit Force Re-ingest`);
                    } else {
                      setRelinkResult(`✗ ${(data as any).error ?? "Relink failed"}`);
                    }
                  } catch { setRelinkResult("✗ Network error"); }
                  finally { setRelinking(false); }
                }}
                className="bg-blue-700 hover:bg-blue-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded px-3 py-1.5 text-xs transition-colors whitespace-nowrap"
              >
                {relinking ? "Linking..." : "Relink"}
              </button>
            </div>
            {relinkResult && (
              <div className={`text-xs rounded px-2 py-1.5 ${relinkResult.startsWith("✓") ? "text-green-400 bg-green-900/20" : "text-red-400 bg-red-900/20"}`}>{relinkResult}</div>
            )}
          </div>

          {/* ── Customer-facing KB search index (separate from copilot) ──── */}
          <div className="border border-slate-700 rounded-lg p-4 space-y-3">
            <div className="text-white text-sm font-semibold">Customer-Facing KB Search Index</div>
            <p className="text-slate-400 text-xs">
              Separate from the copilot pipeline above. This pushes articles into <strong className="text-white">Azure AI Search</strong> for the public <code className="text-green-400 bg-slate-800 px-1 rounded">help.velaro.com</code> search bar and the bot's answer index (Ada-002 vector embeddings). Requires Azure Search + Azure OpenAI to be configured.
            </p>
            <div className="flex gap-2 flex-wrap">
              <button
                disabled={reindexing}
                onClick={async () => {
                  setReindexing(true); setReindexResult(null);
                  try {
                    const res = await api.admin.post("api/kb/search/reindex", {});
                    const data = await res.json().catch(() => ({}));
                    setReindexResult((data as any).message ?? `${(data as any).count ?? 0} articles pushed to Azure Search.`);
                  } catch { setReindexResult("✗ Failed — check server logs"); }
                  finally { setReindexing(false); }
                }}
                className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded px-3 py-1.5 text-xs transition-colors"
              >
                {reindexing ? "Indexing..." : "Re-index (BM25 Search)"}
              </button>
              <button
                disabled={vectorBackfilling}
                onClick={async () => {
                  setVectorBackfilling(true); setVectorResult(null);
                  try {
                    const res = await api.admin.post("api/kb/search/vector-backfill", {});
                    const data = await res.json().catch(() => ({}));
                    setVectorResult((data as any).message ?? "Vector backfill queued.");
                  } catch { setVectorResult("✗ Failed — check server logs"); }
                  finally { setVectorBackfilling(false); }
                }}
                className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded px-3 py-1.5 text-xs transition-colors"
              >
                {vectorBackfilling ? "Queuing..." : "Backfill Vector/AI Index"}
              </button>
            </div>
            {reindexResult && <div className="text-xs text-slate-300 bg-slate-900/60 rounded px-2 py-1.5">{reindexResult}</div>}
            {vectorResult && <div className="text-xs text-slate-300 bg-slate-900/60 rounded px-2 py-1.5">{vectorResult}</div>}
          </div>
        </div>
      )}

      {/* ── Action Log tab ──────────────────────────────────────────────── */}
      {activeTab === "actions" && (
        <div className="space-y-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <h2 className="text-white font-semibold mb-1">Copilot Action Log</h2>
            <p className="text-slate-400 text-sm mb-4">
              Every configuration change made by the Velaro Copilot is recorded here with a before/after snapshot.
              You can roll back any change with one click — the system will revert to the exact state before the copilot acted.
            </p>

            {rollbackMsg && (
              <div className="mb-3 text-sm bg-blue-900/30 border border-blue-700 text-blue-300 rounded px-3 py-2">
                {rollbackMsg}
              </div>
            )}

            {actionLogsLoading ? (
              <div className="text-slate-400 text-sm py-6 text-center">Loading action log...</div>
            ) : actionLogs.length === 0 ? (
              <div className="text-slate-500 text-sm py-6 text-center">
                No copilot actions recorded yet. When the copilot renames a deployment, assigns a team, or makes any other configuration change, it will appear here.
              </div>
            ) : (
              <div className="max-h-[600px] overflow-y-auto space-y-2">
                {actionLogs.map((log) => (
                  <div key={log.id}
                    className={`flex items-start justify-between gap-3 rounded-lg px-4 py-3 border ${
                      log.isRolledBack
                        ? "bg-slate-900/40 border-slate-700 opacity-60"
                        : "bg-slate-900 border-slate-600"
                    }`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono bg-slate-700 text-slate-300 rounded px-1.5 py-0.5">
                          {log.toolName}
                        </span>
                        {log.isRolledBack && (
                          <span className="text-xs bg-amber-900/40 border border-amber-700 text-amber-400 rounded px-1.5 py-0.5">
                            rolled back {log.rolledBackBy ? `by ${log.rolledBackBy}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="text-white text-sm mt-1">{log.summary}</div>
                      <div className="text-slate-500 text-xs mt-1">
                        {new Date(log.createdAt).toLocaleString()} · by {log.triggeredBy || "copilot"}
                      </div>
                    </div>
                    {!log.isRolledBack && (
                      <button
                        onClick={() => rollbackAction(log.id)}
                        disabled={rollingBack === log.id}
                        className="shrink-0 text-xs bg-red-900/30 hover:bg-red-900/60 border border-red-700 text-red-400 hover:text-red-300 rounded px-3 py-1.5 transition-colors disabled:opacity-50"
                      >
                        {rollingBack === log.id ? "Reverting..." : "Roll Back"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <h2 className="text-white font-semibold mb-1">MCP Server — External Access</h2>
            <p className="text-slate-400 text-sm mb-3">
              Expose these tools to external agents via the Velaro MCP endpoint. Requires <span className="text-white font-medium">MCP</span> subscription feature.
            </p>
            <div className="bg-slate-900 rounded p-3 font-mono text-xs text-slate-300 space-y-1">
              <div><span className="text-slate-500">Endpoint:</span> POST /mcp/copilot</div>
              <div><span className="text-slate-500">Protocol:</span> JSON-RPC 2.0</div>
              <div><span className="text-slate-500">Info:</span> GET /mcp/copilot/info</div>
              <div><span className="text-slate-500">Auth:</span> Same session auth as the admin panel</div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Available tools: get_subscription_status · get_deployment_info · get_team_config · get_connected_integrations · get_recent_actions · rename_deployment · assign_team_to_deployment
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
